import { faSort } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React, { useRef } from 'react'
import { useDrag, type ConnectDragSource, type ConnectDropTarget, type ConnectDragPreview } from 'react-dnd'
import { useCollectionsNestingTableContext } from './CollectionsNestingTableContext.js'
import { CollectionsNestingTableNestingRow } from './CollectionsNestingTableNestingRow.js'
import type { CollectionsNestingTableCollection, CollectionsNestingTableItem } from './Types.js'
import { useCollectionsListItemDrop, type CollectionsNestingTableItemDragItem } from './useItemDrop.js'
import { useCollectionListCollectionDrop, type CollectionsNestingTableCollectionDragItem } from './useCollectionDrop.js'
import { observer } from 'mobx-react-lite'

export const CollectionsNestingTableItemRow = observer(function CollectionsNestingTableItemRow<
	TCollection extends CollectionsNestingTableCollection,
	TItem extends CollectionsNestingTableItem,
>({
	item,
	index,
	nestingLevel,
	children,
}: React.PropsWithChildren<{
	item: TItem
	index: number
	nestingLevel: number
}>) {
	const { dragId, collectionsApi, selectedItemId } = useCollectionsNestingTableContext<TCollection, TItem>()

	const { drop } = useCollectionsListItemDrop(collectionsApi, dragId, item.collectionId, item.id, index)
	const [{ isDragging }, drag, preview] = useDrag<
		CollectionsNestingTableItemDragItem,
		unknown,
		{ isDragging: boolean }
	>({
		type: dragId,
		item: {
			itemId: item.id,
			collectionId: item.collectionId,
			index,
			dragState: null,
		},
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	})

	return (
		<CollectionsNestingTableRowBase
			drag={drag}
			drop={drop}
			preview={preview}
			isDragging={isDragging}
			isSelected={item.id === selectedItemId}
			nestingLevel={nestingLevel}
			className="collections-nesting-table-row-item"
		>
			{children}
		</CollectionsNestingTableRowBase>
	)
})

export const CollectionsNestingTableCollectionRowWrapper = observer(
	function CollectionsNestingTableCollectionRowWrapper<TCollection extends CollectionsNestingTableCollection>({
		collection,
		parentId,
		index,
		nestingLevel,
		children,
	}: React.PropsWithChildren<{
		collection: TCollection
		parentId: string | null
		index: number
		nestingLevel: number
	}>) {
		const { dragId, collectionsApi } = useCollectionsNestingTableContext<TCollection, CollectionsNestingTableItem>()

		// Allow dropping items onto the collection, to add them to the collection
		const { drop } = useCollectionsListItemDrop(collectionsApi, dragId, collection.id, null, -1)
		const { drop: collectionDrop } = useCollectionListCollectionDrop(
			collectionsApi,
			dragId,
			parentId,
			index,
			collection.id
		)

		const [{ isDragging }, drag, preview] = useDrag<
			CollectionsNestingTableCollectionDragItem,
			unknown,
			{ isDragging: boolean }
		>({
			type: `${dragId}-collection`,
			item: {
				collectionId: collection.id,
				index,
				parentId: parentId,
				dragState: null,
			},
			collect: (monitor) => ({
				isDragging: monitor.isDragging(),
			}),
		})

		return (
			<CollectionsNestingTableRowBase
				drag={drag}
				drop={(e) => drop(collectionDrop(e))}
				preview={preview}
				isDragging={isDragging}
				isSelected={false}
				nestingLevel={nestingLevel}
				className="collections-nesting-table-row-group"
			>
				{children}
			</CollectionsNestingTableRowBase>
		)
	}
)

function CollectionsNestingTableRowBase({
	className,
	drag,
	drop,
	preview,
	isDragging,
	isSelected,
	nestingLevel,
	children,
}: React.PropsWithChildren<{
	className: string
	drag: ConnectDragSource
	drop: ConnectDropTarget
	preview: ConnectDragPreview
	isDragging: boolean
	isSelected: boolean
	nestingLevel: number
}>) {
	const ref = useRef<HTMLDivElement>(null)
	preview(drop(ref))

	return (
		<div
			className={classNames(className, {
				'row-dragging': isDragging,
				'row-notdragging': !isDragging,
				'row-selected': isSelected,
			})}
			ref={ref}
		>
			<CollectionsNestingTableNestingRow
				className="collections-nesting-table-row-item-grid"
				nestingLevel={nestingLevel}
			>
				<div ref={drag} className="row-reorder-handle">
					<FontAwesomeIcon icon={faSort} />
				</div>
				<div className="grow">{children}</div>
			</CollectionsNestingTableNestingRow>
		</div>
	)
}
